
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SocialMetaTags } from './SocialMetaTags';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
  canonicalUrl?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
}

const SEOHead = ({
  title = "FlatJive: Less Hassle. More Harmony.",
  description = "FlatJive helps users manage share of freehold collaboratively. Manage service charges, maintenance, documents, and decisions in one platform.",
  keywords = "share of freehold, property management, UK, service charges, flat management, freehold collaboration, property maintenance",
  image = "https://flatjive.com/placeholder.svg",
  imageAlt = "FlatJive - Property Management Platform",
  type = "website",
  noIndex = false,
  canonicalUrl,
  publishedTime,
  modifiedTime,
  author,
  section,
  tags
}: SEOHeadProps) => {
  const location = useLocation();
  
  // Get canonical base URL from environment or fallback
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://flatjive.com';
  
  // Detect if current host matches canonical host
  const isCanonicalHost = typeof window !== 'undefined' 
    ? window.location.host === new URL(SITE_URL).host
    : true;
  
  // Use provided canonical URL or build from current path
  // All canonical URLs should already include trailing slashes
  const cleanUrl = canonicalUrl 
    ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${SITE_URL}${canonicalUrl}`)
    : `${SITE_URL}${location.pathname}${location.pathname.endsWith('/') ? '' : '/'}`;

  // Force noindex on non-canonical hosts (staging, preview, www, etc.)
  const shouldNoIndex = noIndex || !isCanonicalHost;

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={cleanUrl} />
        
        {/* Robots */}
        {shouldNoIndex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}
        
        {/* Additional Meta Tags */}
        <meta name="author" content={author || "FlatJive"} />
        <meta name="googlebot" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
      </Helmet>
      
      {/* Enhanced Social Media Tags */}
      <SocialMetaTags
        title={title}
        description={description}
        url={cleanUrl}
        image={image}
        imageAlt={imageAlt}
        type={type}
        publishedTime={publishedTime}
        modifiedTime={modifiedTime}
        author={author}
        section={section}
        tags={tags}
      />
    </>
  );
};

export default SEOHead;
